/**
 * Form validation utilities for Inara platform.
 * Provides real-time character checking, required field enforcement,
 * and country-aware formatting.
 */

export interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

export const SUPPORTED_COUNTRIES = [
  { code: "SA", name: "Saudi Arabia", postalHint: "5 digits (e.g. 12271)" },
  { code: "AE", name: "United Arab Emirates", postalHint: "5 digits or 00000" },
  { code: "US", name: "United States", postalHint: "5 digits or 5+4 (e.g. 90210)" },
  { code: "GB", name: "United Kingdom", postalHint: "e.g. SW1A 1AA" },
  { code: "CA", name: "Canada", postalHint: "e.g. K1A 0B1" },
  { code: "BH", name: "Bahrain", postalHint: "3 or 4 digits" },
  { code: "KW", name: "Kuwait", postalHint: "5 digits" },
  { code: "OM", name: "Oman", postalHint: "3 digits" },
  { code: "QA", name: "Qatar", postalHint: "5 digits or 00000" },
  { code: "EG", name: "Egypt", postalHint: "5 digits" },
  { code: "JO", name: "Jordan", postalHint: "5 digits" },
  { code: "DE", name: "Germany", postalHint: "5 digits" },
  { code: "FR", name: "France", postalHint: "5 digits" },
  { code: "OTHER", name: "Other Country", postalHint: "3-10 alphanumeric characters" },
];

/**
 * Validates name fields (First name, Last name, Cardholder name, Admin name).
 * Enforces letters, spaces, apostrophes, and hyphens only.
 * Explicitly flags numbers and special characters.
 */
export function validateName(value: string, fieldLabel = "Name", isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: `${fieldLabel} is required.` };
    }
    return { isValid: true, error: null };
  }

  // Check for numbers
  if (/\d/.test(value)) {
    return {
      isValid: false,
      error: `${fieldLabel} must accept letters only and cannot contain numbers.`,
    };
  }

  // Check for disallowed special characters (letters, spaces, hyphens, and apostrophes only)
  // Support English, Arabic, and international Latin letters
  const validNameRegex = /^[\p{L}\s'-]+$/u;
  if (!validNameRegex.test(value)) {
    return {
      isValid: false,
      error: `${fieldLabel} must accept letters only and cannot contain special characters.`,
    };
  }

  if (trimmed.length < 2) {
    return {
      isValid: false,
      error: `${fieldLabel} must be at least 2 characters long.`,
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates username fields.
 * Accepts letters and numbers only.
 * Disallows spaces and special characters.
 */
export function validateUsername(value: string, isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: "Username is required." };
    }
    return { isValid: true, error: null };
  }

  if (/\s/.test(value)) {
    return {
      isValid: false,
      error: "Username cannot contain spaces.",
    };
  }

  // Check for special characters - only letters and numbers allowed
  const validUsernameRegex = /^[a-zA-Z0-9]+$/;
  if (!validUsernameRegex.test(value)) {
    return {
      isValid: false,
      error: "Username should allow letters and numbers only, without special characters.",
    };
  }

  if (value.length < 3) {
    return {
      isValid: false,
      error: "Username must be at least 3 characters long.",
    };
  }

  if (value.length > 30) {
    return {
      isValid: false,
      error: "Username cannot exceed 30 characters.",
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates email address.
 */
export function validateEmail(value: string, isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: "Email address is required." };
    }
    return { isValid: true, error: null };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return {
      isValid: false,
      error: "Please enter a valid email address.",
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates password.
 */
export function validatePassword(value: string, isRequired = true): ValidationResult {
  if (!value) {
    if (isRequired) {
      return { isValid: false, error: "Password is required." };
    }
    return { isValid: true, error: null };
  }

  if (value.length < 8) {
    return {
      isValid: false,
      error: "Password must be at least 8 characters long.",
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates Street Address.
 * Accepts letters, numbers, spaces, and standard address symbols (.,-#/&').
 * Prevents invalid/special characters (e.g. <>{}$%^*+=;!?"@\~`).
 */
export function validateAddress(value: string, isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: "Address is required." };
    }
    return { isValid: true, error: null };
  }

  // Check for explicitly dangerous / invalid symbols in addresses
  const invalidAddressCharsRegex = /[<>{}()\[\]^~|$%*+=;!?"@\\`]/;
  if (invalidAddressCharsRegex.test(value)) {
    return {
      isValid: false,
      error: "Address contains invalid characters. Use letters, numbers, spaces, and standard address symbols (., - # / & ').",
    };
  }

  // Address must contain valid alphanumeric characters
  const validAddressRegex = /^[\p{L}\p{N}\s.,\-#/&']+$/u;
  if (!validAddressRegex.test(value)) {
    return {
      isValid: false,
      error: "Address contains invalid symbols. Please check and re-enter.",
    };
  }

  if (trimmed.length < 4) {
    return {
      isValid: false,
      error: "Address must be at least 4 characters long.",
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates City.
 * Accepts letters, spaces, hyphens, and apostrophes.
 */
export function validateCity(value: string, isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: "City is required." };
    }
    return { isValid: true, error: null };
  }

  if (/\d/.test(value)) {
    return {
      isValid: false,
      error: "City cannot contain numbers.",
    };
  }

  const validCityRegex = /^[\p{L}\s'-]+$/u;
  if (!validCityRegex.test(value)) {
    return {
      isValid: false,
      error: "City cannot contain special characters.",
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validates Postal Code according to the selected country format and length.
 * Prevents invalid characters or incorrect lengths.
 */
export function validatePostalCode(value: string, countryCode = "SA", isRequired = true): ValidationResult {
  const trimmed = value.trim();

  if (!trimmed) {
    if (isRequired) {
      return { isValid: false, error: "Postal code is required." };
    }
    return { isValid: true, error: null };
  }

  switch (countryCode.toUpperCase()) {
    case "SA": // Saudi Arabia: 5 digits
      if (!/^\d+$/.test(trimmed)) {
        return { isValid: false, error: "Saudi postal code must contain digits only." };
      }
      if (trimmed.length !== 5) {
        return { isValid: false, error: `Saudi postal code must be exactly 5 digits (currently ${trimmed.length}).` };
      }
      return { isValid: true, error: null };

    case "US": // United States: 5 digits or 5+4 (12345 or 12345-6789)
      if (!/^\d{5}(-\d{4})?$/.test(trimmed)) {
        if (!/^[0-9-]+$/.test(trimmed)) {
          return { isValid: false, error: "US ZIP code must contain numbers and optional hyphen only." };
        }
        return { isValid: false, error: "US ZIP code must be 5 digits (e.g. 90210) or 5+4 format (e.g. 90210-1234)." };
      }
      return { isValid: true, error: null };

    case "GB": // United Kingdom: e.g. SW1A 1AA, EC1A 1BB, W1A 0AX
      const ukRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
      if (!ukRegex.test(trimmed)) {
        return { isValid: false, error: "Invalid UK postcode format (e.g. SW1A 1AA or EC1A 1BB)." };
      }
      return { isValid: true, error: null };

    case "CA": // Canada: A1A 1A1
      const caRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
      if (!caRegex.test(trimmed)) {
        return { isValid: false, error: "Canadian postal code must be in A1A 1A1 format." };
      }
      return { isValid: true, error: null };

    case "AE": // UAE: 5 digits or 00000
    case "QA": // Qatar
      if (!/^\d+$/.test(trimmed)) {
        return { isValid: false, error: "Postal code must contain digits only." };
      }
      if (trimmed.length < 3 || trimmed.length > 6) {
        return { isValid: false, error: "Please enter a valid postal code (or 00000)." };
      }
      return { isValid: true, error: null };

    case "BH": // Bahrain: 3 or 4 digits
    case "OM": // Oman: 3 digits
      if (!/^\d+$/.test(trimmed)) {
        return { isValid: false, error: "Postal code must contain digits only." };
      }
      if (trimmed.length < 3 || trimmed.length > 4) {
        return { isValid: false, error: "Postal code must be 3 or 4 digits." };
      }
      return { isValid: true, error: null };

    case "KW": // Kuwait: 5 digits
    case "EG": // Egypt: 5 digits
    case "JO": // Jordan: 5 digits
    case "DE": // Germany: 5 digits
    case "FR": // France: 5 digits
      if (!/^\d+$/.test(trimmed)) {
        return { isValid: false, error: "Postal code must contain digits only." };
      }
      if (trimmed.length !== 5) {
        return { isValid: false, error: `Postal code must be exactly 5 digits (currently ${trimmed.length}).` };
      }
      return { isValid: true, error: null };

    default: // Other: 3 to 10 alphanumeric characters
      if (/[^a-zA-Z0-9\s-]/.test(trimmed)) {
        return { isValid: false, error: "Postal code can only contain letters, numbers, and hyphens." };
      }
      if (trimmed.length < 3 || trimmed.length > 10) {
        return { isValid: false, error: "Postal code length must be between 3 and 10 characters." };
      }
      return { isValid: true, error: null };
  }
}
